import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "Do I need a lawyer to sell my business in Queensland?",
    a: "There is no law requiring one, but a business sale is not a form-filling exercise the way a simple residential contract can be. The price, the restraint of trade, the GST going-concern clause, employee entitlements and the lease assignment all sit in the contract, and each of them can cost you far more than the legal fee if they are drafted loosely. We act for both sellers and buyers across Queensland.",
  },
  {
    q: "What does a business sale lawyer actually do?",
    a: "For a seller: prepare or review the contract, structure the deposit and settlement terms, draft a restraint that will hold up, get the GST going-concern treatment right, deal with the landlord on lease assignment, handle employee transfers, and settle. For a buyer: run the legal side of due diligence, PPSR searches, licence and franchise transfers, warranties, and the handover and training obligations.",
  },
  {
    q: "How much does it cost to sell a business?",
    a: `We quote a fixed fee for straightforward small business sales once we have seen the contract or the heads of agreement, so you know the cost before you engage us. Complex sales — franchises, liquor or food licences, share sales, or businesses with freehold land attached — are quoted individually. Call ${FIRM.phone} and we will scope it on the phone.`,
  },
  {
    q: "Is it an asset sale or a share sale?",
    a: "Most small Queensland business sales are asset sales: the buyer takes the plant, stock, goodwill, business name and the lease, without inheriting the selling company's history and liabilities. Share sales are sometimes better for sellers on tax, but expose the buyer to everything the company has ever done. Get accounting advice on structure before terms are agreed, not after.",
  },
  {
    q: "Is transfer duty payable on a business sale?",
    a: "Queensland abolished duty on most business asset transfers, but duty can still arise where land or a lease interest forms part of the transaction, and share transactions have their own rules. We confirm the duty position for your structure before you sign.",
  },
  {
    q: "How long does a business sale take to settle?",
    a: "Thirty to sixty days is typical. Due diligence usually runs 21–30 days, landlord consent to assign a lease can add two to four weeks on its own, and licence transfers (liquor, food, franchise) set their own timetable. Build the delay into the contract dates rather than hoping for the best.",
  },
];

const SELLER = [
  "Preparing or reviewing the sale contract and the heads of agreement",
  "Structuring the deposit, holdbacks and settlement adjustments",
  "Drafting a restraint of trade that is enforceable, not decorative",
  "GST going-concern clauses so 10% does not appear on the price",
  "Lease assignment and landlord consent, including retail shop lease disclosure",
  "Employee entitlements, transfers and leave adjustments",
];

const BUYER = [
  "Legal due diligence on financials, contracts, licences and litigation",
  "PPSR searches so you are not buying financed plant",
  "Warranties, indemnities and price adjustment mechanisms",
  "Franchise disclosure documents and the cooling-off period",
  "Liquor, food and trade licence transfers",
  "Handover, training and non-compete obligations from the seller",
];

export const Route = createFileRoute("/business-sales")({
  head: () => ({
    meta: [
      { title: fitTitle(`Business Sale Lawyer Queensland | Buying & Selling a Business | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Queensland business sale lawyers for buyers and sellers. Contracts, due diligence, lease assignment, GST going concern, restraints and settlement — fixed fee quoted up front."),
      },
      { property: "og:title", content: fitTitle(`Business Sale Lawyer Queensland | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Selling or buying a business in Queensland? Solicitor-led advice on the contract, due diligence, lease and settlement."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/business-sales" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/business-sales" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: BusinessSalesPage,
});

function BusinessSalesPage() {
  return (
    <>
      <PageHero
        personSlug="michael-klein"
        eyebrow="Business & commercial"
        title="Business sale lawyers — selling or buying a business in Queensland"
        lead="The price is the easy part. What decides whether the deal was a good one is the contract: the restraint, the lease, the GST clause, the employees and what happens if the numbers do not hold up. We act for sellers and buyers across the Sunshine Coast, Moreton Bay and Queensland."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">Selling a business</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Sellers usually come to us with a signed heads of agreement and a broker's contract. That is
          the point where the terms that matter — restraint, deposit, GST treatment, what you must do
          after settlement — are still negotiable. Once the contract is signed they are not.
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {SELLER.map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Buying a business</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Buyers pay for goodwill — a customer base and a trading history they cannot inspect the way
          they would inspect a building. Due diligence and the conditions in the contract are the only
          protection you get, and both have deadlines.
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {BUYER.map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <div className="mt-16 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Read before you sign</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Our plain-English guide covers the whole transaction — due diligence, the REIQ business sale
            contract, lease assignment, employees, GST and going concern, restraints and settlement.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
            <Link to="/selling-a-business-queensland" className="text-primary underline">
              Selling a business in Queensland
            </Link>
            <Link
              to="/guides/$slug"
              params={{ slug: "buying-or-selling-a-business-in-queensland" }}
              className="text-primary underline"
            >
              Buying or selling a business in Queensland
            </Link>
            <Link to="/business-sale-checklist" className="text-primary underline">
              Download the due diligence checklist
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who acts on your sale" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Questions business sellers and buyers ask
        </h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Get a fixed-fee quote on your business sale" />
        </div>
      </section>

      <CtaBand
        title="Got a contract or heads of agreement?"
        body={`Send it through before you sign, or call ${FIRM.phone} and we will talk it through.`}
      />
      <div className="h-20" />
    </>
  );
}
